import {
  View,
  FlatList,
  ActivityIndicator,
  Text,
  TouchableOpacity,
  Image,
} from "react-native";
import React, { useEffect, useState } from "react";
import customAxios from "../../utils/customAxios";
import i18n from "../../locales/i18n";
import { Toast } from "react-native-toast-notifications";

interface Product {
  id_product: number;
  name: string;
  price_sell: string;
  price_bought: string;
  stock: number;
  photo?: string;
  created_at?: string;
}

interface SliderShowProps {
  onProductSelect?: (product: Product) => void;
  custumQunatity?: string;
}

const SliderShow: React.FC<SliderShowProps> = ({
  onProductSelect,
  custumQunatity,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch products with images from API when component mounts
  useEffect(() => {
    const fetchProductsWithImages = async () => {
      try {
        setLoading(true);
        const res = await customAxios.get(`/sell/withimages`);
        setProducts(res.data || []);
      } catch (error) {
        Toast.show(i18n.t("errorFetchingProducts"), {
          type: "danger",
        });
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsWithImages();
  }, []);

  const handleProductPress = (product: Product) => {
    if (onProductSelect) {
      const productToAdd = {
        ...product,
        quantity: custumQunatity ? parseInt(custumQunatity) : 1,
      };
      onProductSelect(productToAdd);
    }
  };

  // Group products into pairs for 2 rows
  const groupProductsInPairs = (products: Product[]) => {
    const pairs = [];
    for (let i = 0; i < products.length; i += 2) {
      pairs.push(products.slice(i, i + 2));
    }
    return pairs;
  };

  // Helper function to get full image URL
  const getImageUrl = (photo?: string) => {
    if (!photo) return null;

    // If it's already a full URL, return it
    if (photo.startsWith("http://") || photo.startsWith("https://")) {
      return photo;
    }

    // If it's a relative path, construct full URL
    // Assuming the base URL is https://api.khdame.com
    const baseUrl = "https://api.khdame.com";
    const imagePath = photo.startsWith("/") ? photo : `/${photo}`;
    return `${baseUrl}${imagePath}`;
  };

  const renderProductContent = (item: Product) => {
    const imageUrl = getImageUrl(item.photo);

    return (
      <>
        {/* Product Image */}
        {imageUrl ? (
          <Image
            source={{ uri: imageUrl }}
            style={{
              width: 60,
              height: 60,
              borderRadius: 8,
              marginBottom: 6,
            }}
            resizeMode="cover"
            onError={(error) => {}}
          />
        ) : (
          <View
            style={{
              width: 60,
              height: 60,
              backgroundColor: "#F5F5F5",
              borderRadius: 8,
              marginBottom: 6,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text
              style={{ color: "#9E9E9E", fontSize: 10, textAlign: "center" }}
            >
              No Image
            </Text>
          </View>
        )}

        {/* Product Name */}
        <Text
          style={{
            fontSize: 12,
            fontWeight: "600",
            color: "#212121",
            textAlign: "center",
            marginBottom: 3,
            minHeight: 16,
          }}
          numberOfLines={1}
        >
          {item.name}
        </Text>

        {/* Product Price */}
        <Text
          style={{
            fontSize: 11,
            color: "#212121",
            textAlign: "center",
            marginBottom: 6,
            fontWeight: "500",
          }}
        >
          {item.price_sell} {i18n.t("currency")}
        </Text>
      </>
    );
  };

  const renderProductPair = ({ item }: { item: Product[] }) => (
    <View style={{ marginRight: 12 }}>
      {item.map((product) => (
        <TouchableOpacity
          key={product.id_product}
          onPress={() => handleProductPress(product)}
          style={{
            width: 150,
            backgroundColor: "white",
            alignItems: "center",
            marginBottom: 6,
            minHeight: 120,
            borderWidth: 1,
            borderColor: "#1E88E5",
            borderRadius: 8,
            padding: 8,
          }}
        >
          {renderProductContent(product)}
        </TouchableOpacity>
      ))}
    </View>
  );

  if (loading) {
    return (
      <View
        style={{
          width: "100%",
          marginVertical: 16,
          justifyContent: "center",
          alignItems: "center",
          paddingVertical: 32,
        }}
      >
        <ActivityIndicator size="large" color="#1E88E5" />
        <Text style={{ marginTop: 8, color: "#757575" }}>
          {i18n.t("loading")}...
        </Text>
      </View>
    );
  }

  if (products.length === 0) {
    return (
      <View
        style={{
          width: "100%",
          marginVertical: 16,
          justifyContent: "center",
          alignItems: "center",
          paddingVertical: 32,
        }}
      >
        <Text style={{ color: "#757575", textAlign: "center" }}>
          {i18n.t("noproducts")}
        </Text>
      </View>
    );
  }

  return (
    <View style={{ width: "100%", marginVertical: 15 }}>
      <FlatList
        data={groupProductsInPairs(products)} // Show ALL products, not just first 10
        keyExtractor={(item, index) => `pair-${index}`}
        horizontal={true}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 16,
        }}
        renderItem={renderProductPair}
      />
    </View>
  );
};

export default SliderShow;
